import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth, API, formatApiErrorDetail } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  DotsThree,
  Globe,
  Check,
  Lock
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

export function DomainsPage() {
  const { user } = useAuth();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingDomain, setEditingDomain] = useState(null);
  const [formData, setFormData] = useState({
    base_domain: '',
    routing_type: 'path',
    is_primary: false,
    ssl_enabled: true,
    description: ''
  });
  const [saving, setSaving] = useState(false);

  const fetchDomains = async () => {
    try {
      const { data } = await axios.get(`${API}/domains?active_only=false`, { withCredentials: true });
      setDomains(data);
    } catch (error) {
      console.error('Failed to fetch domains:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDomains();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/domains`, formData, { withCredentials: true });
      toast.success('Domain added successfully');
      setShowCreateModal(false);
      setFormData({ base_domain: '', routing_type: 'path', is_primary: false, ssl_enabled: true, description: '' });
      fetchDomains();
    } catch (error) {
      console.error('Failed to create domain:', error);
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to add domain');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/domains/${editingDomain.id}`, formData, { withCredentials: true });
      toast.success('Domain updated successfully');
      setEditingDomain(null);
      setFormData({ base_domain: '', routing_type: 'path', is_primary: false, ssl_enabled: true, description: '' });
      fetchDomains();
    } catch (error) {
      console.error('Failed to update domain:', error);
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to update domain');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this domain?')) return;
    try {
      await axios.delete(`${API}/domains/${id}`, { withCredentials: true });
      toast.success('Domain deleted successfully');
      fetchDomains();
    } catch (error) {
      console.error('Failed to delete domain:', error);
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Cannot delete domain that is in use');
    }
  };

  const openEditModal = (domain) => {
    setFormData({
      base_domain: domain.base_domain,
      routing_type: domain.routing_type,
      is_primary: domain.is_primary,
      ssl_enabled: domain.ssl_enabled,
      description: domain.description || ''
    });
    setEditingDomain(domain);
  };

  const filteredDomains = domains.filter(d =>
    d.base_domain.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-zinc-900">Domains</h1>
            <p className="text-zinc-500 mt-1">Manage campaign domains and URL routing</p>
          </div>
          {user?.role === 'super_admin' && (
            <Button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
              data-testid="create-domain-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Domain
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
            <Input
              placeholder="Search domains..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 input-swiss"
              data-testid="domain-search"
            />
          </div>
        </div>

        {/* Domains Table */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-zinc-100 animate-pulse rounded-sm" />
            ))}
          </div>
        ) : filteredDomains.length > 0 ? (
          <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-200">
                  <th className="table-header text-left">Domain</th>
                  <th className="table-header text-left">Routing</th>
                  <th className="table-header text-center">SSL</th>
                  <th className="table-header text-center">Primary</th>
                  <th className="table-header text-center">Status</th>
                  <th className="table-header text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDomains.map((domain) => (
                  <tr key={domain.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`domain-row-${domain.base_domain.replace(/\./g, '-')}`}>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#002FA7]/10 rounded-sm flex items-center justify-center">
                          <Globe className="w-5 h-5 text-[#002FA7]" />
                        </div>
                        <div>
                          <p className="font-medium text-zinc-900">{domain.base_domain}</p>
                          <p className="text-sm text-zinc-500">{domain.description || 'No description'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-zinc-600 capitalize">
                      {domain.routing_type}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {domain.ssl_enabled ? (
                        <Lock className="w-5 h-5 text-emerald-500 mx-auto" />
                      ) : (
                        <span className="text-zinc-400">-</span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {domain.is_primary && (
                        <Check className="w-5 h-5 text-[#002FA7] mx-auto" />
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className={`px-2 py-1 rounded-sm text-xs font-medium ${domain.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-500'}`}>
                        {domain.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-right">
                      {user?.role === 'super_admin' && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-2 hover:bg-zinc-100 rounded-sm" data-testid={`domain-menu-${domain.base_domain.replace(/\./g, '-')}`}>
                              <DotsThree className="w-5 h-5" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditModal(domain)}>
                              <PencilSimple className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(domain.id)} className="text-red-600">
                              <Trash className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 bg-white border border-zinc-200 rounded-sm">
            <Globe className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-500">No domains configured</p>
          </div>
        )}

        {/* Create/Edit Modal */}
        <Dialog open={showCreateModal || !!editingDomain} onOpenChange={() => {
          setShowCreateModal(false);
          setEditingDomain(null);
          setFormData({ base_domain: '', routing_type: 'path', is_primary: false, ssl_enabled: true, description: '' });
        }}>
          <DialogContent className="modal-brutalist sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">
                {editingDomain ? 'Edit Domain' : 'Add Domain'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={editingDomain ? handleUpdate : handleCreate}>
              <div className="space-y-4 py-4">
                <div>
                  <Label className="label-uppercase">Base Domain</Label>
                  <Input
                    value={formData.base_domain}
                    onChange={(e) => setFormData({ ...formData, base_domain: e.target.value })}
                    className="input-swiss"
                    placeholder="e.g., campaigns.example.com"
                    required
                    disabled={!!editingDomain}
                    data-testid="domain-base-input"
                  />
                </div>
                <div>
                  <Label className="label-uppercase">Routing Type</Label>
                  <Select
                    value={formData.routing_type}
                    onValueChange={(v) => setFormData({ ...formData, routing_type: v })}
                  >
                    <SelectTrigger data-testid="domain-routing-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="path">Path-based (/campaign-slug)</SelectItem>
                      <SelectItem value="subdomain">Subdomain (slug.domain)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-uppercase">Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="input-swiss"
                    placeholder="Optional description"
                    data-testid="domain-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="ssl_enabled"
                      checked={formData.ssl_enabled}
                      onChange={(e) => setFormData({ ...formData, ssl_enabled: e.target.checked })}
                      className="rounded border-zinc-300"
                    />
                    <Label htmlFor="ssl_enabled" className="text-sm font-normal cursor-pointer">
                      SSL/HTTPS Enabled
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_primary"
                      checked={formData.is_primary}
                      onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })}
                      className="rounded border-zinc-300"
                    />
                    <Label htmlFor="is_primary" className="text-sm font-normal cursor-pointer">
                      Set as primary domain
                    </Label>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => {
                  setShowCreateModal(false);
                  setEditingDomain(null);
                }}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" disabled={saving} data-testid="domain-save-btn">
                  {saving ? 'Saving...' : (editingDomain ? 'Update' : 'Add Domain')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
